import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { api, ApiError } from '../api/client';
import type {
  BackfillRunResponse,
  BatchCreateRequest,
  BatchCreateResponse,
  ConvergencePrepAccepted,
  TectonicModelCode,
} from '../api/types';
import { TECTONIC_MODEL_LABELS } from '../api/types';
import { addActivePrep } from '../lib/activePreps';
import { cn } from '../lib/classnames';
import { MapScreen } from '../screens/MapScreen';
import { BackfillModeForm } from './BackfillModeForm';
import { validateObsDates } from './batchValidation';
import { ConvergenceModeForm } from './ConvergenceModeForm';
import { RetryModelPicker } from './RetryModelPicker';
import './BatchModal.css';

// Phase 4.5 — default tectonic model matches the PPP-Online service's
// own default. The dropdown lets the operator pick another.
const DEFAULT_TECTONIC_MODEL: TectonicModelCode = 'NNR_MORVEL56';

// Phase 4.5 — default reduction epoch (PPP service standard).
const DEFAULT_REDUCTION_DATE = '2011-01-01';

/** "Quick" GNSS-config presets we show by default. The other 11
 *  combos (gps+gal, glo+gal, triples) are reachable via the
 *  ``ещё ▾`` toggle. Same 4-default set as
 *  ``DEFAULT_GNSS_CONFIGS_LIST`` in the CLI. */
const DEFAULT_GNSS_CONFIGS: readonly string[] = ['gps', 'gps+glo', 'gps+gal', 'all4'] as const;

/** Extended list — pairs / triples / bds-singles. Same labels as the
 *  CLI's ``ALL_GNSS_CONFIGS`` minus the 4 already shown. */
const EXTRA_GNSS_CONFIGS: readonly string[] = [
  'glo',
  'gal',
  'bds',
  'gps+bds',
  'glo+gal',
  'glo+bds',
  'gal+bds',
  'gps+glo+gal',
  'gps+glo+bds',
  'gps+gal+bds',
  'glo+gal+bds',
] as const;

/** Phase 6.3 — added `'backfill'` mode. Spec §C.1 renamed the
 *  selector label from «Источник станций» to «Режим серии»; the
 *  third tab renders a self-contained `BackfillModeForm` instead
 *  of reusing the random/stations fields. Phase C-ui adds the 4th
 *  tab `'convergence'` for slice-based convergence-test runs. */
type Mode = 'random' | 'stations' | 'backfill' | 'convergence';

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function parseDates(raw: string): { ok: true; dates: string[] } | { ok: false; error: string } {
  const lines = raw
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (lines.length === 0) return { ok: false, error: 'Укажи хотя бы одну дату.' };
  const bad = lines.filter((s) => !/^\d{4}-\d{2}-\d{2}$/.test(s));
  if (bad.length > 0) return { ok: false, error: `Некорректные даты: ${bad.join(', ')}` };
  return { ok: true, dates: lines };
}

/** Modal overlay with the new-batch form. Visible only when ``open``
 *  is true — parent keeps that state. ``onCreated`` fires with the
 *  successful API response so the FeedScreen can invalidate its
 *  ``jobs`` / ``status`` queries and surface a confirmation alert.
 */
export function BatchModal({
  open,
  onClose,
  onCreated,
  onConvergenceAccepted,
  initialStationCodes,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (resp: BatchCreateResponse) => void;
  /** Wave 4b — convergence mode now creates asynchronously: the form
   *  gets a 202 (no job_ids yet) instead of the sync ``series`` body,
   *  so it can't go through ``onCreated``'s BatchCreateResponse bridge.
   *  The batch is registered for «готовлю N/M» polling internally; this
   *  OPTIONAL hook lets the parent run extras (invalidate/toast/haptic)
   *  on top. */
  onConvergenceAccepted?: (resp: ConvergencePrepAccepted) => void;
  /** Phase 5 D — when supplied, the modal opens in `stations` mode
   *  with the textarea pre-populated by the given codes (space-
   *  separated). Used by the map screen's «+ Запустить серию для
   *  AMC4» action and by Phase 5.1 multi-select. The prop is
   *  intentionally a snapshot — the modal reads it on mount /
   *  re-open and doesn't re-sync if the parent mutates it later
   *  (avoids overwriting an in-progress edit). */
  initialStationCodes?: string[];
}) {
  const [datesRaw, setDatesRaw] = useState<string>(todayIso());
  const [mode, setMode] = useState<Mode>(
    initialStationCodes && initialStationCodes.length > 0
      ? 'stations'
      : 'random',
  );
  const [randomN, setRandomN] = useState<string>('20');
  const [stationsRaw, setStationsRaw] = useState<string>(
    initialStationCodes && initialStationCodes.length > 0
      ? initialStationCodes.join(' ')
      : '',
  );
  // Phase 5.1 — «выбрать на карте» picker overlay.
  const [mapPickerOpen, setMapPickerOpen] = useState(false);
  const [gnssConfigs, setGnssConfigs] = useState<string[]>(['gps']);
  const [showExtra, setShowExtra] = useState(false);
  const [tag, setTag] = useState<string>('');
  const [localError, setLocalError] = useState<string | null>(null);
  // Phase 4.7 — opt-in auto-substitute. Visible only in ``random``
  // mode (the explicit-stations flow has no sensible semantics for
  // substituting one specific user-picked station for another).
  const [autoSubstitute, setAutoSubstitute] = useState<boolean>(false);
  // Phase 4.15 — per-batch cap on substitution chain depth.
  // Range 1-10, default 5. Visible only when autoSubstitute is on.
  // The value is read off each failing Job's row by the scheduler
  // (every Job in the batch carries the same stamp).
  const [maxAttempts, setMaxAttempts] = useState<number>(5);
  // Phase 4.5 — tectonic model + reduction epoch + auto-retry chain.
  // Default model NNR_MORVEL56 (PPP-Online's own default — flipped
  // from the pre-4.5 hard-coded ITRF2014). Reduction date default
  // 2011-01-01 (PPP standard).
  const [tectonicModel, setTectonicModel] = useState<TectonicModelCode>(
    DEFAULT_TECTONIC_MODEL,
  );
  const [reductionDate, setReductionDate] = useState<string>(
    DEFAULT_REDUCTION_DATE,
  );
  const [autoRetryEnabled, setAutoRetryEnabled] = useState<boolean>(false);
  const [retryModelOrder, setRetryModelOrder] = useState<TectonicModelCode[]>(
    [],
  );
  // If the operator flips the primary model AFTER selecting retry
  // candidates, drop the now-conflicting selection so the picker
  // doesn't render a stale priority badge. ``useMemo`` instead of
  // ``useEffect`` keeps the cleanup synchronous with render — no
  // intermediate frame where the picker shows the excluded model.
  const retryOrderClean = useMemo(
    () => retryModelOrder.filter((m) => m !== tectonicModel),
    [retryModelOrder, tectonicModel],
  );

  const createMut = useMutation({
    mutationFn: (payload: BatchCreateRequest) => api.createBatch(payload),
    onSuccess: (resp) => {
      onCreated(resp);
    },
  });

  const stationsList = useMemo(
    () => stationsRaw.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean),
    [stationsRaw],
  );

  // Phase 3.10.1 — soft + hard date validation. ``parseDates`` already
  // catches malformed ISO strings; the helper below adds future / pre-2000
  // hard blocks and a "today / yesterday → RINEX may be missing" warning.
  const dateValidation = useMemo(() => {
    const parsed = parseDates(datesRaw);
    if (!parsed.ok) return { errors: [], warnings: [] };
    return validateObsDates(parsed.dates);
  }, [datesRaw]);

  // Phase 4.14 — pre-batch pool inspection. Fires only in random mode
  // when the parsed dates are valid; in stations mode the pool size is
  // irrelevant (the picker doesn't run). The query key includes the
  // dates string + model so a date edit invalidates the cache. Keep
  // the cache short (60s) — the operator typically commits within
  // seconds; we don't want to serve a stale "47 excluded" number a
  // background sweep just changed.
  const parsedDatesForPool = useMemo(() => {
    const parsed = parseDates(datesRaw);
    return parsed.ok ? parsed.dates : null;
  }, [datesRaw]);

  const poolQuery = useQuery({
    queryKey: [
      'pool-availability',
      parsedDatesForPool?.join(','),
      tectonicModel,
    ],
    queryFn: () =>
      api.getPoolAvailability({
        obs_dates: parsedDatesForPool ?? [],
        tectonic_model: tectonicModel,
      }),
    enabled:
      open &&
      mode === 'random' &&
      parsedDatesForPool !== null &&
      parsedDatesForPool.length > 0,
    staleTime: 60_000,
  });

  const canSubmit =
    tag.trim().length > 0 &&
    gnssConfigs.length > 0 &&
    !createMut.isPending &&
    dateValidation.errors.length === 0 &&
    (mode === 'random' ? Number(randomN) > 0 : stationsList.length > 0);

  const onSubmit = () => {
    setLocalError(null);
    const parsed = parseDates(datesRaw);
    if (!parsed.ok) {
      setLocalError(parsed.error);
      return;
    }
    const payload: BatchCreateRequest = {
      dates: parsed.dates,
      gnss_configs: gnssConfigs,
      tag: tag.trim(),
      // Phase 4.5 — tectonic model + reduction epoch are always sent
      // (the API has defaults but the form always has explicit
      // values). ``auto_retry_models`` only travels when the
      // checkbox is on AND the picker has at least one selection;
      // otherwise we send null so the backend validator stays happy.
      tectonic_model: tectonicModel,
      reduction_date: reductionDate,
      auto_retry_models:
        autoRetryEnabled && retryOrderClean.length > 0
          ? retryOrderClean
          : null,
      ...(mode === 'random'
        ? {
            random_n: Number(randomN),
            // Phase 4.7 — only meaningful in random mode. Omitted
            // (falsy) when the checkbox is off so legacy operators
            // see no change in the wire payload.
            ...(autoSubstitute
              ? {
                  auto_substitute: true,
                  // Phase 4.15 — only carry the cap when
                  // auto_substitute is on. The clamp at state-set
                  // time already enforces [1, 10]; the API
                  // re-validates via Pydantic Field(ge=1, le=10).
                  max_substitution_attempts: maxAttempts,
                }
              : {}),
          }
        : { station_codes: stationsList.map((s) => s.toUpperCase()) }),
    };
    createMut.mutate(payload);
  };

  if (!open) return null;

  const apiError = createMut.error;
  const errorMsg =
    localError ??
    (apiError instanceof ApiError
      ? `Ошибка ${apiError.status}: ${apiError.message}`
      : apiError instanceof Error
        ? apiError.message
        : null);

  return (
    <div className="modal-backdrop" role="dialog" aria-modal onClick={onClose}>
      <div className="modal-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="grip" />
        <h2>Новая серия</h2>

        {/* Phase C-ui-v2 A — the multi-date textarea is meaningless
            for convergence (one daily RINEX → many slices, one date
            only). ConvergenceModeForm renders its own single-date
            picker below the mode toggle, so we hide this top block
            in convergence to stop the «two date fields with drifting
            values» bug operators hit on the v1 release. */}
        {mode !== 'convergence' && (
        <div className="modal-field" data-testid="batch-modal-date-multi">
          <label>Дата(ы) наблюдений</label>
          <textarea
            rows={2}
            value={datesRaw}
            onChange={(e) => setDatesRaw(e.target.value)}
            placeholder="YYYY-MM-DD по одной на строку"
          />
          <span className="hint">несколько дат → матрица по каждой</span>
          {dateValidation.errors.map((err) => (
            <div key={err} className="modal-error" role="alert">
              {err}
            </div>
          ))}
          {dateValidation.warnings.map((warn) => (
            <div key={warn} className="modal-warning" role="status">
              {warn}
            </div>
          ))}
        </div>
        )}

        <div className="modal-field">
          {/* Phase 6.3 — relabel "Источник станций" → "Режим серии"
              now that the third tab («Восполнение») isn't a source
              choice but a different workflow entirely. */}
          <label>Режим серии</label>
          <div className="mode-toggle" role="tablist">
            <button
              role="tab"
              aria-selected={mode === 'random'}
              className={cn(mode === 'random' && 'active')}
              onClick={() => setMode('random')}
            >
              случайные
            </button>
            <button
              role="tab"
              aria-selected={mode === 'stations'}
              className={cn(mode === 'stations' && 'active')}
              onClick={() => setMode('stations')}
            >
              по кодам
            </button>
            <button
              role="tab"
              aria-selected={mode === 'backfill'}
              className={cn(mode === 'backfill' && 'active')}
              onClick={() => setMode('backfill')}
              data-testid="mode-toggle-backfill"
            >
              восполнение
            </button>
            <button
              role="tab"
              aria-selected={mode === 'convergence'}
              className={cn(mode === 'convergence' && 'active')}
              onClick={() => setMode('convergence')}
              data-testid="mode-toggle-convergence"
            >
              сходимость
            </button>
          </div>
        </div>

        {/* Phase 6.3 — when «Восполнение» is selected, render the
            dedicated form. It owns its own state + preview fetch +
            submit handler; the legacy random/stations fields below
            are hidden so the modal doesn't render two sets of
            inputs at once. */}
        {mode === 'backfill' && (
          <BackfillModeForm
            onCreated={(resp: BackfillRunResponse) => {
              // The Mini App's existing post-create plumbing keys
              // on BatchCreateResponse-shaped objects; the backfill
              // response carries the same {job_ids, created, tag}
              // trio so we can map directly without a new branch
              // upstream.
              onCreated({
                job_ids: resp.job_ids,
                created: resp.created,
                skipped_duplicates: 0,
                stations_added: [],
                tag: resp.tag,
              });
            }}
          />
        )}

        {/* Wave 4b — convergence creates ASYNC. The form returns the
            202 (batch_id + viable_count + note/skipped), shows its own
            «принято, готовлю N» panel, and on «Готово» calls
            ``onAccepted``. There are no job_ids yet (the scheduler
            daemon builds them in the background), so this bypasses the
            BatchCreateResponse bridge above and goes straight to the
            parent's prep-registry handler. */}
        {mode === 'convergence' && (
          <ConvergenceModeForm
            onAccepted={(resp) => {
              // Register for «готовлю N/M» polling regardless of which
              // screen opened the modal (the registry is global localStorage,
              // so the Feed's prep card picks it up on next view).
              addActivePrep({
                batch_id: resp.batch_id,
                total: resp.total,
                created_at: new Date().toISOString(),
              });
              onConvergenceAccepted?.(resp);
              onClose();
            }}
          />
        )}

        {/* Phase 6.3 / Phase C-ui — hide the legacy random/stations
            form when a self-contained mode (backfill / convergence)
            is active. Each of those owns its own state + submit, so
            rendering both would dup the GNSS chip picker and the
            Cancel/Create buttons. */}
        {mode !== 'backfill' && mode !== 'convergence' && (
        <>
        {mode === 'random' ? (
          <>
            <div className="modal-info" role="note">
              {/* Phase 3.18 — replaced the Phase-3.10.1 plate. The
                  original text quoted a 20% success rate and a "3 из 6"
                  IGS data-centre fraction; both numbers are stale after
                  Phase 4.0 / 4.1.2 lifted the success rate to ~75-80%
                  and the centre count to 7 (BKG, CDDIS, GA, IBGE, IGN,
                  SOPAC, WHU). The new wording avoids commitments to
                  any specific rate and just states the data-centre
                  count, which is the only stable, operator-relevant
                  fact about the random-mode resolver today. */}
              ℹ️ В режиме случайной выборки часть станций может оказаться
              временно недоступной. Подключено 7 IGS data-центров.
            </div>
            {/* Phase 4.14 — show how many stations are currently
                sampleable for the requested (dates, model). Empty
                state (loading or no dates yet) renders nothing — the
                operator sees the counter appear once the query
                resolves. ``excluded > 0`` is the interesting case;
                we still render the line at 0 so the UX is
                predictable. */}
            {poolQuery.data && (
              <div
                className="modal-info modal-pool-counter"
                role="note"
                data-testid="pool-availability"
              >
                📊 Доступно: {poolQuery.data.available} (исключено{' '}
                {poolQuery.data.excluded} как закешированных недоступными)
              </div>
            )}
            <div className="modal-field">
              <label>Сколько станций (random_n)</label>
              <input
                type="number"
                min={1}
                max={500}
                value={randomN}
                onChange={(e) => setRandomN(e.target.value)}
                className="gnss-qa-input"
              />
              <span className="hint">IGS-каталог · 1-500 · исключает уже виданных</span>
            </div>
            {/* Phase 4.7 — auto-substitute checkbox. Visible only in
                random mode; the explicit-stations flow above doesn't
                render this block at all. When ticked, the scheduler
                replaces any rinex-not-available failure with a clone
                of the job pointing at a fresh random station (up to
                3 substitution attempts per chain). */}
            <div className="modal-field modal-field-checkbox">
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={autoSubstitute}
                  onChange={(e) => setAutoSubstitute(e.target.checked)}
                  className="gnss-qa-checkbox"
                />
                <span>Автозамена недоступных станций</span>
              </label>
              <span className="hint">
                Если RINEX-файл станции не найден ни в одном из 7 IGS
                data-центров, система автоматически подберёт другую
                станцию (макс. {maxAttempts} попыт{maxAttempts === 1
                  ? 'ка'
                  : maxAttempts < 5
                    ? 'ки'
                    : 'ок'} на исходную задачу).
              </span>
            </div>
            {/* Phase 4.15 — per-batch cap on substitution chain
                depth. Visible only when autoSubstitute is on; the
                clamp on setMaxAttempts enforces the [1, 10] range
                synchronously so an out-of-bounds input never reaches
                the createBatch payload (API also re-validates via
                Pydantic Field(ge=1, le=10) as a defence in depth). */}
            {autoSubstitute && (
              <div className="modal-field modal-field-inline">
                <label htmlFor="batch-max-attempts">
                  Максимум попыток автозамены:
                </label>
                <input
                  id="batch-max-attempts"
                  type="number"
                  min={1}
                  max={10}
                  value={maxAttempts}
                  className="gnss-qa-input num-input-small"
                  onChange={(e) => {
                    // Three input flavours, distinct branches:
                    //   Empty (cleared field or HTML5 type=number
                    //     rejected non-digit input, which jsdom
                    //     surfaces as '') → fall back to the spec
                    //     default 5. Matches the «default 5» hint.
                    //   Numeric → clamp to [1, 10] so '0' lands at
                    //     1 and '99' lands at 10.
                    //   NaN belt-and-suspenders for direct programmatic
                    //     mutation outside the type=number gate.
                    const raw = e.target.value;
                    if (raw === '') {
                      setMaxAttempts(5);
                      return;
                    }
                    const n = Number(raw);
                    if (Number.isNaN(n)) {
                      setMaxAttempts(5);
                      return;
                    }
                    setMaxAttempts(Math.max(1, Math.min(10, n)));
                  }}
                />
                <span className="hint">1-10 · по умолчанию 5</span>
              </div>
            )}
          </>
        ) : (
          <div className="modal-field">
            <label>Коды станций (4 символа)</label>
            {/* Phase 5.1 — «Выбрать на карте» picker shortcut.
                Parses the current textarea via the same
                whitespace/comma regex stationsList uses, hands
                the list to MapScreen as initialSelection, and
                writes the confirmed array back into the textarea
                on «Готово». Two-way sync — operator can either
                type codes or pick on the map, and round-trip
                between the two flows freely. */}
            <button
              type="button"
              className="batch-modal-map-picker-btn"
              onClick={() => setMapPickerOpen(true)}
              data-testid="batch-modal-map-picker-open"
            >
              Выбрать на карте →
            </button>
            <textarea
              rows={3}
              value={stationsRaw}
              onChange={(e) => setStationsRaw(e.target.value)}
              placeholder="WTZR POTS GOPE ZIM2 BOR1"
            />
            <span className="hint">через пробел / перевод строки · {stationsList.length} шт.</span>
          </div>
        )}

        <div className="modal-field">
          <label>GNSS конфигурации</label>
          <div className="gnss-chip-row">
            {DEFAULT_GNSS_CONFIGS.map((cfg) => {
              const active = gnssConfigs.includes(cfg);
              return (
                <button
                  key={cfg}
                  type="button"
                  className={cn(active && 'active')}
                  onClick={() =>
                    setGnssConfigs((cur) =>
                      cur.includes(cfg) ? cur.filter((c) => c !== cfg) : [...cur, cfg],
                    )
                  }
                >
                  {cfg}
                </button>
              );
            })}
            <button
              type="button"
              className="more-toggle"
              onClick={() => setShowExtra((v) => !v)}
            >
              {showExtra ? 'свернуть' : 'ещё ▾'}
            </button>
            {showExtra &&
              EXTRA_GNSS_CONFIGS.map((cfg) => {
                const active = gnssConfigs.includes(cfg);
                return (
                  <button
                    key={cfg}
                    type="button"
                    className={cn(active && 'active')}
                    onClick={() =>
                      setGnssConfigs((cur) =>
                        cur.includes(cfg) ? cur.filter((c) => c !== cfg) : [...cur, cfg],
                      )
                    }
                  >
                    {cfg}
                  </button>
                );
              })}
          </div>
          <span className="hint">{gnssConfigs.length} выбрано · jobs = dates × stations × configs</span>
        </div>

        {/* Phase 4.5 — tectonic model dropdown. Default
            NNR_MORVEL56 (PPP-Online's own default). */}
        <div className="modal-field">
          <label htmlFor="batch-tectonic-model">Модель тектоники</label>
          <select
            id="batch-tectonic-model"
            value={tectonicModel}
            onChange={(e) =>
              setTectonicModel(e.target.value as TectonicModelCode)
            }
            data-testid="batch-tectonic-model"
            className="gnss-qa-select"
          >
            <option value="NNR_MORVEL56">
              {TECTONIC_MODEL_LABELS.NNR_MORVEL56} (по умолчанию)
            </option>
            <option value="NUVEL_1A">{TECTONIC_MODEL_LABELS.NUVEL_1A}</option>
            <option value="ITRF2014">{TECTONIC_MODEL_LABELS.ITRF2014}</option>
            <option value="GRSM_21">{TECTONIC_MODEL_LABELS.GRSM_21}</option>
          </select>
        </div>

        {/* Phase 4.5 — auto-retry with alternative tectonic models.
            Checkbox unfolds the RetryModelPicker. */}
        <div className="modal-field modal-field-checkbox">
          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={autoRetryEnabled}
              onChange={(e) => setAutoRetryEnabled(e.target.checked)}
              data-testid="batch-auto-retry-checkbox"
              className="gnss-qa-checkbox"
            />
            <span>Авто-повтор при plate-outside с другими моделями</span>
          </label>
          {autoRetryEnabled && (
            <>
              <span className="hint">
                Модели пробуются по очереди от 1 к N. Перед каждой
                попыткой проверяется кэш недоступности — известные
                неудачи пропускаются.
              </span>
              <RetryModelPicker
                excludedModel={tectonicModel}
                selectedOrder={retryOrderClean}
                onChange={setRetryModelOrder}
              />
            </>
          )}
        </div>

        {/* Phase 4.5 — reduction epoch. Default 01.01.2011 (PPP
            standard). Wrapped in a small wrapper so the hint sits
            below the input with the same rhythm as auto_substitute. */}
        <div className="modal-field">
          <label htmlFor="batch-reduction-date">Эпоха приведения</label>
          <input
            id="batch-reduction-date"
            type="date"
            value={reductionDate}
            onChange={(e) => setReductionDate(e.target.value)}
            data-testid="batch-reduction-date"
            className="gnss-qa-date"
          />
          <span className="hint">
            Стандартная эпоха PPP-сервиса 01.01.2011. Менять только при
            явном понимании последствий.
          </span>
        </div>

        <div className="modal-field">
          <label>Тег</label>
          <input
            type="text"
            value={tag}
            maxLength={64}
            onChange={(e) => setTag(e.target.value)}
            placeholder="например, miniapp_test_v1"
            className="gnss-qa-input"
          />
        </div>

        {errorMsg && <div className="modal-error">{errorMsg}</div>}

        <div className="modal-actions">
          <button onClick={onClose} disabled={createMut.isPending}>
            Отмена
          </button>
          <button
            className="primary"
            onClick={onSubmit}
            disabled={!canSubmit}
            type="button"
          >
            {createMut.isPending ? 'Создаю…' : 'Создать'}
          </button>
        </div>
        </>
        )}
      </div>
      {/* Phase 5.1 — fullscreen map picker overlay. Mounted only
          when the operator clicked «Выбрать на карте» in stations
          mode. Parses the current textarea content into
          initialSelection so already-typed codes are pre-ringed.
          `onConfirm(codes)` writes the sorted array back to the
          textarea; `onCancel` drops the picker without touching
          state. */}
      {mapPickerOpen && (
        <div
          className="batch-modal-map-picker-overlay"
          data-testid="batch-modal-map-picker-overlay"
        >
          <MapScreen
            selectionMode={{
              initialSelection: stationsRaw
                .split(/[\s,]+/)
                .map((s) => s.trim())
                .filter(Boolean),
              onConfirm: (codes) => {
                setStationsRaw(codes.join(' '));
                setMapPickerOpen(false);
              },
              onCancel: () => setMapPickerOpen(false),
            }}
          />
        </div>
      )}
    </div>
  );
}
